import AppCommandBar from 'app/AppCommandBar'
import { FlexSpinner } from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { StickyPane } from 'components/StickyPane'
import { ResourceCategory, ResourceCategoryContext } from 'context'
import { ProvingGroundsContext } from 'context/ProvingGroundsContext'
import { useCommand, useCustomSearchCommand } from 'hooks/useCommand'
import { useRouteParams } from 'hooks/useRouteParams'
import { sortBy } from 'lodash-es'
import { UserRole } from 'model'
import * as React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AdminResourcesListView } from './AdminResourcesListView'
import { useReadViewDataQuery } from './AdminResourcesView.generated'
import { ResourceCategoryPagination } from './ResourceCategoryPagination'

export interface RouteParams {
  search: string | undefined
  pgid: string
  category: string
  type: string
  parentID?: string
  provingsList?: boolean
  id?: string
}

export const AdminResourcesView: React.FunctionComponent = () => {
  const { t } = useTranslation()
  const { loading, data, error } = useReadViewDataQuery()
  const provingGrounds = data && data.provingGrounds ? data.provingGrounds : []
  const [treeViewFilterState, setTreeViewFilterState] = useState<string>('')
  const [displayProvingItems, setDisplayProvingItems] = React.useState(false)

  const resourceCategories: ResourceCategory[] = React.useMemo(
    () =>
      data && data.resourceCategories
        ? sortBy(
            data.resourceCategories
              .filter((resCategory) => resCategory._id !== 'provingGround')
              .map((resCategory) => ({
                id: resCategory._id,
                title: t(`field.category.${resCategory._id}`),
                types: sortBy(
                  resCategory.types.map((type) => ({
                    id: type,
                    title: t(`field.type.${type}`, {
                      category: t(`field.category.${resCategory._id}`),
                    }),
                  })),
                  'title'
                ),
              })),
            'title'
          )
        : [],
    [data, t]
  )

  const [routeParams, updateRouteParams] = useRouteParams<RouteParams>(
    'admin.data.resources',
    {
      search: undefined,
      pgid: provingGrounds[0] ? provingGrounds[0]._id : '',
      category: resourceCategories[0] ? resourceCategories[0].id : '',
      type: 'all',
    },
    { persist: ['pgid', 'category'] }
  )
  const provingGround =
    provingGrounds.filter((x) => x._id === routeParams.pgid)[0] ||
    provingGrounds[0]

  useCustomSearchCommand(
    {
      value: routeParams.search,
      placeholder: t('model.resource.search'),
      onSearch: (value) => updateRouteParams({ search: value, type: 'all' }),
      onClear: () => {
        updateRouteParams({ search: undefined })
      },
    },
    [routeParams, updateRouteParams]
  )

  useCommand(
    {
      priority: 15,
      key: 'provingGround',
      farCommand: true,
      text: provingGround ? provingGround.abbr : '',
      iconProps: { iconName: 'POISolid' },
      subMenuProps: {
        items: provingGrounds.map((pg) => ({
          key: `provingGround-${pg._id}`,
          name: `${pg.title} (${pg.abbr})`,
          onClick: () => {
            updateRouteParams({ pgid: pg._id, search: undefined })
          },
        })),
      },
    },
    [provingGround, provingGrounds, updateRouteParams]
  )
  const resetFilteringStates = () => {
    setDisplayProvingItems(false)
    setTreeViewFilterState('')
  }
  return (
    <ViewAuthorizer
      roles={[UserRole.ResourceManager, UserRole.SystemAdministrator]}
    >
      <FlexSpinner loading={loading} error={error}>
        <StickyPane>
          <AppCommandBar />
          <ResourceCategoryContext.Provider value={resourceCategories}>
            <ProvingGroundsContext.Provider value={provingGrounds}>
              <ResourceCategoryPagination
                selection={routeParams}
                onChange={updateRouteParams}
                onResetFiltering={resetFilteringStates}
              />
              <React.Suspense fallback={<FlexSpinner />}>
                <AdminResourcesListView
                  routeParams={routeParams}
                  updateRouteParams={updateRouteParams}
                  filterState={treeViewFilterState}
                  setFilterState={setTreeViewFilterState}
                  displayProvingItems={displayProvingItems}
                  setDisplayProvingItems={setDisplayProvingItems}
                />
              </React.Suspense>
            </ProvingGroundsContext.Provider>
          </ResourceCategoryContext.Provider>
        </StickyPane>
      </FlexSpinner>
    </ViewAuthorizer>
  )
}
